import { AxiosResponse } from 'axios';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'libs/Axios';
import qs from 'query-string';

import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import {
  CubeDefinitionDetails,
  CubeDefinitionDiagramAllResponse,
  CubeDefinitionDropdownPanelOptions,
  CubeDefinitionEventAttributeResponse,
  CubeDefinitionItem,
  CubeDefinitionTimePeriodSetResponse,
  CubeMemberMatchField,
  CubeMemberMatchPreviewRequest,
  CubeMemberMatchPreviewResponse,
  PullCubeDropdownItem,
  RequestCopyCubeDefinition,
  RequestCreateCubeDefinition,
  ResponseCopyCubeDefinition,
  ResponseCubeCopyDataCheck,
} from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { ENV_CONFIG } from 'config/env';

export const CUBE_BUILDER_QUERY_KEY = {
  DEFINITION_LIST: 'CUBE_DEFINIITON_LIST',
  DROPDOWNS_PANEL: 'CUBE_DROPDOWNS_PANEL',
  CUBE_DEFINITION_DETAILS: 'CUBE_DEFINITION_DETAILS',
  CUBE_EVENT_ATTRIBUTES_LIST: 'CUBE_EVENT_ATTRIBUTES_LIST',
  CUBE_DEFINITION_DIAGRAM_ALL: 'CUBE_DEFINITION_DIAGRAM_ALL',
  PULL_CUBE_DROPDOWN: 'PULL_CUBE_DROPDOWN',
  PULL_CUBE_DROPDOWN_LAYER: 'PULL_CUBE_DROPDOWN_LAYER',
  CUBE_DEFINITION_TIME_PERIOD_SET: 'CUBE_DEFINITION_TIME_PERIOD_SET',
  COPY_DATA_CHECK: 'CUBE_COPY_DATA_CHECK',
  MEMBER_MATCH_FIELDS: 'CUBE_MEMBER_MATCH_FIELDS',
  MAP_SOURCE_ATTRIBUTES: 'CUBE_MAP_SOURCE_ATTRIBUTES',
};

export function useGetCubeMemberMatchFields(subjectId?: string | number) {
  return useQuery<AxiosResponse<CubeMemberMatchField[]>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.MEMBER_MATCH_FIELDS, subjectId],
    () =>
      axios.get(
        `/api/v1/cube-definition/member-match-fields?${qs.stringify({
          subjectId,
        })}`
      ),
    { enabled: !!subjectId }
  );
}

export function useGetCubeMapSourceAttributes(cubeId?: string | null) {
  return useQuery<AxiosResponse<DropdownItem[]>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.MAP_SOURCE_ATTRIBUTES, cubeId],
    () => axios.get(`/api/v1/cube-definition/${encodeURIComponent(cubeId!)}/map-source-attributes`),
    { enabled: !!cubeId }
  );
}

export function usePreviewCubeMemberMatches() {
  return useMutation<
    AxiosResponse<CubeMemberMatchPreviewResponse>,
    AxiosDefaultErrorEntity,
    CubeMemberMatchPreviewRequest
  >((payload) => axios.post('/api/v1/cube-definition/member-match-preview', payload), { retry: false });
}

export function useGetPullCubeDropdown({
  subjectId,
  attributeId,
  layerSetId,
  timePeriodSetId,
  timePeriodType,
}: {
  subjectId?: string | number;
  attributeId?: string | number;
  layerSetId?: string | number;
  timePeriodSetId?: string | number;
  timePeriodType?: string;
}) {
  return useQuery<AxiosResponse<PullCubeDropdownItem[]>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.PULL_CUBE_DROPDOWN, subjectId, attributeId, layerSetId, timePeriodSetId, timePeriodType],
    () =>
      axios
        .get(
          `/api/v1/cube-definition/pull-cube/dropdown?${qs.stringify({
            subjectId,
            attributeId,
            layerSetId,
            timePeriodSetId,
            timePeriodType,
          })}`
        )
        .then((res) => res)
  );
}

export function useGetPullCubeLayerDropdown(cubeDefinitionId?: string) {
  return useQuery<AxiosResponse<DropdownItem[]>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.PULL_CUBE_DROPDOWN_LAYER, cubeDefinitionId],
    () => axios.get(`/api/v1/cube-definition/pull-cube/dropdown/layer/${cubeDefinitionId}`).then((res) => res),
    {
      enabled: !!cubeDefinitionId,
    }
  );
}

export function useGetCubeEventAttributeList(subjectId?: string | number) {
  return useQuery<AxiosResponse<CubeDefinitionEventAttributeResponse>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.DEFINITION_LIST, subjectId],
    () => axios.get(`/api/v1/cube-definition/event-attribute/${subjectId}`).then((res) => res),
    {
      enabled: !!subjectId,
      refetchOnMount: true,
    }
  );
}

export function useGetCubeEventAttributeListMutation() {
  return useMutation<
    AxiosResponse<CubeDefinitionEventAttributeResponse>,
    AxiosDefaultErrorEntity,
    { subjectId?: string | number }
  >((payload) => axios.get(`/api/v1/cube-definition/event-attribute/${payload.subjectId}`).then((res) => res));
}

export function useGetCubeDefinitionTimePeriodSet(cubeDefinitionId?: string) {
  return useQuery<AxiosResponse<CubeDefinitionTimePeriodSetResponse>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.CUBE_DEFINITION_TIME_PERIOD_SET, cubeDefinitionId],
    () => axios.get(`/api/v1/cube-definition/time-period-set/${cubeDefinitionId}`).then((res) => res),
    {
      enabled: !!cubeDefinitionId,
    }
  );
}

export function useGetCubeDefinitionDetails(cubeId?: string, options?: { refetchInterval: number | false }) {
  return useQuery<AxiosResponse<CubeDefinitionDetails>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.CUBE_DEFINITION_DETAILS, cubeId],
    () => axios.get(`/api/v1/cube-definition/${cubeId}`).then((res) => res),
    {
      enabled: !!cubeId,
      refetchInterval: options?.refetchInterval,
      cacheTime: !options?.refetchInterval ? ENV_CONFIG.endpoint.MAXIMUM_CACHE_TIME : undefined,
      staleTime: !options?.refetchInterval ? ENV_CONFIG.endpoint.MAXIMUM_CACHE_TIME : undefined,
    }
  );
}

export function useGetAllCubeDefinitionList() {
  return useQuery<AxiosResponse<CubeDefinitionItem[]>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.DEFINITION_LIST],
    () => axios.get(`/api/v1/cube-definition/list`).then((res) => res),
  );
}

export function useCopyCubeDefinition() {
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<ResponseCopyCubeDefinition>, AxiosDefaultErrorEntity, RequestCopyCubeDefinition>(
    (payload) => axios.post('/api/v1/cube-definition/copy', payload),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(CUBE_BUILDER_QUERY_KEY.DEFINITION_LIST);
        queryClient.invalidateQueries(CUBE_BUILDER_QUERY_KEY.CUBE_DEFINITION_DIAGRAM_ALL);
        queryClient.invalidateQueries(CUBE_BUILDER_QUERY_KEY.DROPDOWNS_PANEL);
        queryClient.invalidateQueries(CUBE_BUILDER_QUERY_KEY.PULL_CUBE_DROPDOWN);
      },
    }
  );
}

export function useRetryCubeCopyData() {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<ResponseCopyCubeDefinition>, AxiosDefaultErrorEntity, string>(
    (cubeId) => axios.post(`/api/v1/cube-definition/${encodeURIComponent(cubeId)}/copy-data/retry`),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(CUBE_BUILDER_QUERY_KEY.DEFINITION_LIST);
        queryClient.invalidateQueries(CUBE_BUILDER_QUERY_KEY.CUBE_DEFINITION_DIAGRAM_ALL);
      },
    }
  );
}

export function useCheckCubeCopyData(payload: RequestCopyCubeDefinition, enabled: boolean) {
  return useQuery<AxiosResponse<ResponseCubeCopyDataCheck>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.COPY_DATA_CHECK, payload],
    ({ signal }) => axios.post('/api/v1/cube-definition/copy-data-check', payload, { signal }),
    {
      enabled,
      retry: false,
      cacheTime: 0,
      staleTime: 0,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );
}

export function useGetDropdownsCubePanel(subjectId?: string | number, currentCubeDefinitionId?: string) {
  return useQuery<AxiosResponse<CubeDefinitionDropdownPanelOptions>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.DROPDOWNS_PANEL, subjectId, currentCubeDefinitionId],
    () =>
      axios
        .get(
          `/api/v1/cube-definition/dropdown/${subjectId}?${qs.stringify({
            currentCubeId: currentCubeDefinitionId,
          })}`
        )
        .then((res) => res),
    {
      enabled: !!subjectId,
    }
  );
}

export function useExportCubeDefinition() {
  return useMutation<AxiosResponse, AxiosDefaultErrorEntity, { cubeDefinitionId: string; fileType: string }>(
    (payload) =>
      axios.get(`/api/v1/cube-definition/export/${payload.cubeDefinitionId}?${qs.stringify({ fileType: payload.fileType })}`, {
        responseType: 'blob',
      })
  );
}

export function useDownloadCubeDefinitionTemplate() {
  return useMutation<AxiosResponse, AxiosDefaultErrorEntity, void>(() =>
    axios.get('/api/v1/cube-definition/import/template', {
      responseType: 'blob',
    })
  );
}

export interface ImportCubeDefinitionResponse {
  cubeDefinitionId: string;
  message: string;
  warnings?: string[];
}

export function useImportCubeDefinition(subjectId?: string) {
  return useMutation<AxiosResponse<ImportCubeDefinitionResponse>, AxiosDefaultErrorEntity, FormData>((formData) =>
    axios.post(`/api/v1/cube-definition/import/${subjectId}`, formData)
  );
}

export function useUpsertCubeDefinition(subjectId?: string | number) {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosDefaultErrorEntity, RequestCreateCubeDefinition>(
    (data) => axios.post(`/api/v1/cube-definition/upsert/${subjectId}`, data).then((res) => res),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(CUBE_BUILDER_QUERY_KEY.DEFINITION_LIST);
        queryClient.invalidateQueries(CUBE_BUILDER_QUERY_KEY.CUBE_DEFINITION_DETAILS);
      },
    }
  );
}

export function useGetCubeDefinitionDiagramAll({
  layout = 'LR',
  attributeIds = [],
  dataFlowTypes = [],
  enabled = false,
}: {
  layout?: string;
  attributeIds?: Array<string | number>;
  dataFlowTypes?: Array<string | number>;
  enabled?: boolean;
} = {}) {
  const normalizedAttributeIds = attributeIds
    .map((attributeId) => attributeId?.toString())
    .filter((attributeId): attributeId is string => !!attributeId)
    .sort();
  const normalizedDataFlowTypes = dataFlowTypes
    .map((dataFlowType) => dataFlowType?.toString())
    .filter((dataFlowType): dataFlowType is string => !!dataFlowType)
    .sort();

  return useQuery<AxiosResponse<CubeDefinitionDiagramAllResponse>, AxiosDefaultErrorEntity>(
    [CUBE_BUILDER_QUERY_KEY.CUBE_DEFINITION_DIAGRAM_ALL, layout, normalizedAttributeIds, normalizedDataFlowTypes],
    () => {
      const searchParams = new URLSearchParams({ layout });

      normalizedAttributeIds.forEach((attributeId) => {
        searchParams.append('attributeIds', attributeId);
      });
      normalizedDataFlowTypes.forEach((dataFlowType) => {
        searchParams.append('dataFlowTypes', dataFlowType);
      });

      return axios.get(`/api/v1/cube-definition/diagram/all?${searchParams.toString()}`).then((res) => res);
    },
    {
      enabled,
      keepPreviousData: true,
    }
  );
}
